import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

declare var bootstrap: any;

interface DroneManufacturer {
  id: string;
  name: string;
  description: string | null;
  url: string | null;
  logoUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

interface DroneModel {
  id: string;
  manufacturerId: string;
  modelName: string;
  description: string | null;
  maxTakeoffWeightLbs: number | null;
  imageUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

@Component({
  selector: 'app-manufacturer-detail',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './manufacturer-detail.component.html',
  styleUrls: ['./manufacturer-detail.component.scss']
})
export class ManufacturerDetailComponent implements OnInit {
  manufacturerId!: string;
  manufacturer: DroneManufacturer | null = null;
  models: DroneModel[] = [];
  loading = true;
  loadingModels = false;
  errorMessage = '';
  archiving = false;
  archivingModelId: string | null = null;
  modelToArchive: DroneModel | null = null;
  private archiveModal: any;
  private archiveModelModal: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.manufacturerId = this.route.snapshot.params['id'];
    this.loadManufacturer();
  }

  async loadManufacturer(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: DroneManufacturer }>(
          `${environment.apiUrl}/admin/drone-manufacturers/${this.manufacturerId}`
        )
      );

      if (response.success && response.data) {
        this.manufacturer = response.data;
        this.loadModels();
      }
    } catch (error: any) {
      console.error('Failed to load manufacturer:', error);
      this.errorMessage = 'Failed to load manufacturer details';
    } finally {
      this.loading = false;
    }
  }

  async loadModels(): Promise<void> {
    this.loadingModels = true;

    try {
      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: DroneModel[] }>(
          `${environment.apiUrl}/admin/drone-models?manufacturerId=${this.manufacturerId}`
        )
      );

      if (response.success && response.data) {
        this.models = response.data;
      }
    } catch (error: any) {
      console.error('Failed to load models:', error);
      // Non-critical - don't show error to user
    } finally {
      this.loadingModels = false;
    }
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  formatWeight(weight: number | null): string {
    if (weight === null || weight === undefined) return '-';
    return `${weight.toLocaleString()} lbs`;
  }

  isOver55Lbs(weight: number | null): boolean {
    return weight !== null && weight > 55;
  }

  get modelsOver55LbsCount(): number {
    return this.models.filter(m => this.isOver55Lbs(m.maxTakeoffWeightLbs)).length;
  }

  goBack(): void {
    this.router.navigate(['/drone-catalog']);
  }

  openWebsite(): void {
    if (this.manufacturer?.url) {
      window.open(this.manufacturer.url, '_blank');
    }
  }

  editManufacturer(): void {
    this.router.navigate(['/drone-catalog', this.manufacturerId, 'edit']);
  }

  addModel(): void {
    this.router.navigate(['/drone-catalog', this.manufacturerId, 'models', 'create']);
  }

  editModel(model: DroneModel): void {
    this.router.navigate(['/drone-catalog', this.manufacturerId, 'models', model.id, 'edit']);
  }

  confirmArchiveModel(model: DroneModel): void {
    this.modelToArchive = model;
    const modalElement = document.getElementById('archiveModelModal');
    if (modalElement) {
      this.archiveModelModal = new bootstrap.Modal(modalElement);
      this.archiveModelModal.show();
    }
  }

  async archiveModel(): Promise<void> {
    if (!this.modelToArchive) return;

    this.archivingModelId = this.modelToArchive.id;

    try {
      await firstValueFrom(
        this.http.delete(
          `${environment.apiUrl}/admin/drone-models/${this.modelToArchive.id}`
        )
      );

      if (this.archiveModelModal) {
        this.archiveModelModal.hide();
      }

      // Remove from list and reload
      this.models = this.models.filter(m => m.id !== this.modelToArchive?.id);
      this.modelToArchive = null;
    } catch (error: any) {
      console.error('Failed to archive model:', error);
      this.errorMessage = 'Failed to archive model. Please try again.';
      if (this.archiveModelModal) {
        this.archiveModelModal.hide();
      }
    } finally {
      this.archivingModelId = null;
    }
  }

  confirmArchive(): void {
    const modalElement = document.getElementById('archiveModal');
    if (modalElement) {
      this.archiveModal = new bootstrap.Modal(modalElement);
      this.archiveModal.show();
    }
  }

  async archiveManufacturer(): Promise<void> {
    this.archiving = true;

    try {
      await firstValueFrom(
        this.http.delete(
          `${environment.apiUrl}/admin/drone-manufacturers/${this.manufacturerId}`
        )
      );

      if (this.archiveModal) {
        this.archiveModal.hide();
      }
      this.router.navigate(['/drone-catalog']);
    } catch (error: any) {
      console.error('Failed to archive manufacturer:', error);
      this.errorMessage = 'Failed to archive manufacturer. Please try again.';
      if (this.archiveModal) {
        this.archiveModal.hide();
      }
    } finally {
      this.archiving = false;
    }
  }
}
